// Department Configuration - UNIFIED APPROACH
// Manages organizational departments (Quality, Production, Engineering, etc.)
// Based on Prisma Department model schema

import { createTrackingConfig } from '../../components/generic/labels';
import { appConfig, getDateFormat, tableConfig, uiConfig } from '../../config/appConfig';

export const departmentConfig = {
  entityName: 'Department',
  
  // Backend API Configuration
  api: {
    baseEndpoint: '/department',
    endpoints: {
      list: '',
      create: '',
      update: (id) => `/${id}`,
      delete: (id) => `/${id}`,
      getById: (id) => `/${id}`,
      // Department specific endpoints
      activate: (id) => `/${id}/activate`,
      deactivate: (id) => `/${id}/deactivate`,
      reorder: '/reorder'
    },
    lookupEndpoints: {
      // Departments typically don't have lookup dependencies
      // but you could add department types, regions, etc.
    }
  },
  
  // UI Configuration - Using centralized app config
  ui: {
    drawerWidth: '600px', // Custom for departments (smaller than default)
    defaultPageSize: tableConfig.defaultPageSize, // From app config: 10
    pageSizeOptions: tableConfig.pageSizeOptions, // From app config: [10, 25, 50, 100]
    defaultTableDensity: tableConfig.defaultDensity, // From app config: 'normal'
    requiredFieldStyle: uiConfig.requiredFieldStyle // From app config: 'badge'
  },
  
  // Service configuration
  service: {
    name: 'departmentService',
    methods: {
      getAll: 'getAllDepartments',
      getById: 'getDepartmentById', 
      create: 'createDepartment',
      update: 'updateDepartment',
      delete: 'deleteDepartment',
      validate: 'validateData'
    }
  },

  // UNIFIED FIELD CONFIGURATION
  fields: [
    // Basic Information
    { 
      name: 'id', 
      label: 'ID',
      type: 'hidden',
      table: false,
      filterable: false
    },
    { 
      name: 'name', 
      label: 'Department Name', 
      type: 'text', 
      required: true,
      placeholder: 'Enter department name',
      table: { header: 'Department Name', width: '200px', sortable: true },
      filterable: true,
      validation: { 
        required: true, 
        minLength: 2, 
        maxLength: 100,
      }
    },
    { 
      name: 'isActive', 
      label: 'Active', 
      type: 'switch', 
      required: false,
      defaultValue: true,
      table: { 
        header: 'Status', 
        width: '100px', 
        sortable: true,
        cellRenderer: 'boolean'
      },
      filterable: true,
      validation: { required: false },
      switchLabel: 'Department is active and available for use',
      description: 'Inactive departments cannot be assigned to new records'
    },
    { 
      name: 'record_order', 
      label: 'Display Order', 
      type: 'number', 
      required: false,
      placeholder: 'Enter display order (optional)',
      table: { header: 'Order', width: '80px', sortable: true },
      filterable: false,
      validation: { 
        required: false,
        min: 1,
        max: 999,
        integer: true
      },
      helperText: 'Controls the order in dropdown lists and displays'
    },
    { 
      name: 'color', 
      label: 'Display Color', 
      type: 'color', 
      required: false,
      placeholder: 'Choose a color or enter hex/name',
      table: { 
        header: 'Color', 
        width: '100px', 
        sortable: false,
        cellRenderer: 'color'
      },
      filterable: false,
      validation: { 
        required: false,
        pattern: /^(#[0-9A-Fa-f]{6}|#[0-9A-Fa-f]{3}|[a-zA-Z]+)$/,
        message: 'Please enter a valid color (hex code or color name)'
      },
      helperText: 'Used for visual identification in charts and UI elements'
    },
    
    // Tracking Information (display only)
    { 
      name: 'createdAt', 
      label: 'Created Date', 
      type: 'date', 
      readonly: true,
      table: { 
        header: 'Created', 
        width: tableConfig.dateColumnWidth,
        sortable: true,
        cellRenderer: 'date',
        dateFormat: getDateFormat('table')
      },
      filterable: true,
      filterType: 'dateRange'
    },
    { 
      name: 'updatedAt', 
      label: 'Last Updated', 
      type: 'date', 
      readonly: true,
      table: { 
        header: 'Updated', 
        width: tableConfig.dateColumnWidth,
        sortable: true,
        cellRenderer: 'date',
        dateFormat: getDateFormat('table')
      },
      filterable: true,
      filterType: 'dateRange'
    }
  ],

  // Table Configuration - Dynamic getter that generates columns from fields
  get tableColumns() {
    return this.fields
      .filter(field => field.table !== false && !field.hidden)
      .map(field => ({
        key: field.name,
        header: field.table?.header || field.label,
        width: field.table?.width || 'auto',
        sortable: field.table?.sortable !== false,
        displayField: field.table?.displayField || field.name,
        cellRenderer: field.table?.cellRenderer || 'default'
      }));
  },

  // Filter Configuration
  filterableFields: ['name', 'isActive', 'createdAt', 'updatedAt'],

  // Filter Groups for Basic/Advanced tabs
  filterGroups: [
    {
      name: 'basic',
      label: 'Basic Filters',
      fields: ['name', 'isActive']
    },
    {
      name: 'advanced',
      label: 'Advanced Filters',
      fields: ['record_order', 'color', 'createdAt', 'updatedAt']
    }
  ],

  // Custom Sort Orders
  customSortOrders: {
    isActive: [true, false], // Active departments first
    record_order: 'asc' // Ascending order by default
  },

  // Messages Configuration
  messages: {
    loading: 'Loading departments...',
    noData: 'No departments found. Click "Add Department" to create the first department.',
    loadError: 'Failed to load departments',
    saveSuccess: 'Department saved successfully!',
    saveError: 'Failed to save department',
    deleteSuccess: 'Department deleted successfully!',
    deleteError: 'Failed to delete department',
    deleteConfirm: 'Are you sure you want to delete this department? This action cannot be undone and may affect associated documents, CAPAs, complaints, deviations, and non-conformity reports.',
    activateSuccess: 'Department activated successfully!',
    deactivateSuccess: 'Department deactivated successfully!'
  },

  // Form Layout Configuration
  layout: {
    mode: 'sections',
    sections: [
      {
        id: 'basic',
        title: 'Department Information',
        icon: '🏢',
        columns: 2,
        fields: [
          { name: 'name' },
          { name: 'isActive' },
          { name: 'record_order' },
          { name: 'color' }
        ]
      }
    ]
  },

  // Tracking Section Configuration
  trackingSection: createTrackingConfig('Department'),

  // Labels Configuration
  labels: {
    // Using predefined labels + custom overrides
    singular: 'Department',
    plural: 'Departments',
    addButton: 'Add Department',
    editButton: 'Edit Department',
    deleteButton: 'Delete Department',
    
    // Custom labels for departments
    pageTitle: 'Department Management',
    pageSubtitle: 'Manage organizational departments and their settings',
    searchPlaceholder: 'Search departments by name...',
    
    // Form labels
    basicInfoSection: 'Department Information',
    
    // Action labels
    activateDepartment: 'Activate Department',
    deactivateDepartment: 'Deactivate Department',
    reorderDepartments: 'Reorder Departments'
  },

  // Fields to remove during data preparation for submission (used by BaseService)
  fieldsToRemove: [
    // Remove system fields that should not be updated
    'id',
    'createdAt',
    'updatedAt', 
    'createdById',
    'updatedById',
    // Remove all relation fields
    'documents',
    'capas', 
    'complaints',
    'deviations',
    'nonConformityReports'
  ],

  // Export filename for data exports
  exportFileName: 'departments',


  // Validation function for real-time field validation (dynamically assigned)
  validateField: null // Will be set after validation module loads
};
